import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { getCompanySettings, updateCompanySettings, type CompanySettings } from '@/lib/settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ChevronLeft, Sparkles, Snowflake, Leaf, Cherry, Users } from 'lucide-react';

export const Route = createFileRoute('/settings/customization')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'customization' } });
  },
});

function getSeason() {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
}

const seasonInfo = {
  spring: {
    label: 'Spring',
    description: 'Cherry blossom petals',
    icon: Cherry,
    color: 'text-pink-400',
  },
  summer: {
    label: 'Summer',
    description: 'No effects active',
    icon: Sparkles,
    color: 'text-yellow-400',
  },
  autumn: { label: 'Autumn', description: 'Falling leaves', icon: Leaf, color: 'text-orange-400' },
  winter: { label: 'Winter', description: 'Snowflakes', icon: Snowflake, color: 'text-blue-300' },
};

export function CustomizationPage() {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery<CompanySettings>({
    queryKey: ['companySettings'],
    queryFn: getCompanySettings,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<Pick<CompanySettings, 'seasonalEffectsEnabled' | 'employeeNameDisplayFormat'>>) =>
      updateCompanySettings({
        name: settings!.name,
        timezone: settings!.timezone,
        website: settings!.website || undefined,
        seasonalEffectsEnabled: patch.seasonalEffectsEnabled ?? settings!.seasonalEffectsEnabled,
        employeeNameDisplayFormat: patch.employeeNameDisplayFormat ?? settings!.employeeNameDisplayFormat,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companySettings'] });
      queryClient.invalidateQueries({ queryKey: ['appearanceSettings'] });
    },
  });

  const season = getSeason();
  const currentSeason = seasonInfo[season];
  const SeasonIcon = currentSeason.icon;

  return (
    <div>
      <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/settings" className="hover:text-foreground transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <Link to="/settings" className="hover:text-foreground transition-colors">
            Settings
          </Link>
          <span>/</span>
          <span className="font-medium text-foreground">Appearance</span>
        </div>
      </header>

      <div className="p-6 max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Appearance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visual customization and seasonal decorations
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Seasonal Effects</CardTitle>
              <CardDescription>
                Animated decorations that change with the seasons — cherry blossom petals in spring,
                falling leaves in autumn, and snowflakes in winter.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label htmlFor="seasonal-toggle" className="text-sm font-medium">
                      Enable seasonal effects
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Applies to all users in the organization
                    </p>
                  </div>
                </div>
                <Switch
                  id="seasonal-toggle"
                  checked={settings?.seasonalEffectsEnabled ?? true}
                  onCheckedChange={(checked) => updateMutation.mutate({ seasonalEffectsEnabled: checked })}
                  disabled={updateMutation.isPending || !settings}
                />
              </div>

              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <SeasonIcon className={`h-4 w-4 ${currentSeason.color}`} />
                  <span className="font-medium">Current season: {currentSeason.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {settings?.seasonalEffectsEnabled
                    ? currentSeason.description
                    : 'Effects are currently disabled'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Name Display</CardTitle>
              <CardDescription>
                How employee names are shown in lists and search. Applies to all users in the organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Label htmlFor="name-order-select" className="text-sm font-medium">Name order</Label>
                    <p className="text-xs text-muted-foreground">Used across employee lists, pickers and search</p>
                  </div>
                </div>
                <Select
                  value={settings?.employeeNameDisplayFormat ?? 'first_last'}
                  onValueChange={(value) =>
                    updateMutation.mutate({ employeeNameDisplayFormat: value as CompanySettings['employeeNameDisplayFormat'] })
                  }
                  disabled={updateMutation.isPending || !settings}
                >
                  <SelectTrigger id="name-order-select" className="w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_last">First name Last name</SelectItem>
                    <SelectItem value="last_first">Last name First name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
